import { TemplateControl } from '../request-result-modal.types';
import { ControlValueTranslator } from '../utils/control-value-translator.util';
import { DateFormatLangPipe } from '@app/utils/pipes/date-format-lang-pipe';

/**
 * Service class for getting control values with proper formatting and translation
 */
export class ControlValueService {
  constructor(private dateFormatPipe: DateFormatLangPipe) {}

  /**
   * Get formatted control value for display
   */
  getValue(control: TemplateControl, lang: string): string {
    let value = '';
    const resultValue = this.getResultValueAsString(control);

    // Priority 1: Use resultValues if exists and not empty
    if (resultValue) {
      value = resultValue;

      // Check if this is a dropdown with semicolon-separated options
      if (ControlValueTranslator.hasSemicolonSeparatedValues(control)) {
        // Translate the result value to the current language
        const translatedValue = ControlValueTranslator.translateDropdownValue(control, value, lang);
        if (translatedValue) {
          return translatedValue;
        }
      }
    } else {
      // Priority 2: Use language-specific values with fallback
      value = ControlValueTranslator.getValueWithFallback(control, lang);
    }

    // If no value found anywhere, return default
    if (!value || value === '') {
      return '-';
    }

    // Format dates if needed (supports mixed enum formats: string/number/casing)
    if (this.isDateLikeControlType(control.type) && value) {
      const includeTime = this.isDateTimeLikeControlType(control.type);
      const formatted = this.dateFormatPipe.transform(value, lang, includeTime);
      return formatted || value;
    }

    return value;
  }

  /**
   * Get image path from control, resolving from extracted files if needed
   */
  getImagePath(control: TemplateControl, extractedFiles: Map<string, string | Blob>): string {
    const imageFilename = (control.values_uz || control.values_en || control.values_ru || this.getResultValueAsString(control)) as string;

    if (imageFilename && !imageFilename.startsWith('http') && !imageFilename.startsWith('blob:')) {
      const extractedFile = extractedFiles.get(imageFilename);
      if (extractedFile && typeof extractedFile === 'string') {
        return extractedFile;
      }
    }

    return imageFilename || '';
  }

  getImagePaths(control: TemplateControl, extractedFiles: Map<string, string | Blob>, maxCount = 5): string[] {
    const multiRaw = this.getResultValueAsString(control);
    if (multiRaw?.includes(';')) {
      const filenames = multiRaw.split(';').map(s => s.trim()).filter(Boolean).slice(0, maxCount);
      return filenames.map(fn => {
        if (!fn.startsWith('http') && !fn.startsWith('blob:')) {
          const extracted = extractedFiles.get(fn);
          if (extracted && typeof extracted === 'string') return extracted;
        }
        return fn;
      });
    }
    const single = this.getImagePath(control, extractedFiles);
    return single ? [single] : [];
  }

  /**
   * Convert control value for table display (e.g., checkbox to boolean)
   */
  getValueForTable(control: TemplateControl, lang: string): any {
    const value = this.getValue(control, lang);

    // Convert checkbox string values to boolean for table display
    if (control.type === 'checkbox') {
      return value === 'checked' || value === 'true';
    }

    return value;
  }

  private getResultValueAsString(control: TemplateControl): string {
    return typeof control.resultValues === 'string' ? control.resultValues : '';
  }

  private isDateLikeControlType(value: unknown): boolean {
    if (typeof value === 'number') {
      return value === 6 || value === 7;
    }
    if (typeof value === 'string') {
      const normalized = value.trim().toLowerCase();
      return normalized === 'date' || normalized === 'datetime' || normalized === '6' || normalized === '7';
    }
    return false;
  }

  private isDateTimeLikeControlType(value: unknown): boolean {
    if (typeof value === 'number') {
      return value === 7;
    }
    if (typeof value === 'string') {
      const normalized = value.trim().toLowerCase();
      return normalized === 'datetime' || normalized === '7';
    }
    return false;
  }
}

